import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  RiskMatrixType,
  RISK_MATRIX_TYPE_LABELS,
  RISK_LEVEL_COLORS,
  RISK_LEVEL_SHORT_LABELS,
  getRiskMatrix,
  getProbabilityLabels,
  getSeverityLabels,
  getMaxProbability,
  getMaxSeverity,
} from '../../../../types/riskMatrix';

interface RiskMatrixGridProps {
  matrixType: RiskMatrixType;
  /** Currently selected probability (1-based), highlighted in the grid. */
  selectedProbability?: number | null;
  /** Currently selected severity (1-based), highlighted in the grid. */
  selectedSeverity?: number | null;
}

const AXIS_COL_WIDTH = 104;

const headerCellSx = {
  p: 0.75,
  textAlign: 'center' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1.15,
};

/**
 * Read-only reference grid for the selected risk matrix (BS 8800 4x3 / AIHA 5x5).
 * Rows are probability, columns are severity; each cell is coloured by its
 * resulting risk level. The currently selected probability x severity cell is
 * highlighted. Issue #307.
 */
export const RiskMatrixGrid: React.FC<RiskMatrixGridProps> = ({
  matrixType,
  selectedProbability,
  selectedSeverity,
}) => {
  const matrix = getRiskMatrix(matrixType);
  const probLabels = getProbabilityLabels(matrixType);
  const sevLabels = getSeverityLabels(matrixType);
  const maxProb = getMaxProbability(matrixType);
  const maxSev = getMaxSeverity(matrixType);

  const probabilities = Array.from({ length: maxProb }, (_, i) => i + 1);
  const severities = Array.from({ length: maxSev }, (_, i) => i + 1);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Matriz de risco de referência — {RISK_MATRIX_TYPE_LABELS[matrixType]}
      </Typography>
      <Box
        role="grid"
        aria-label="Matriz de risco de referência"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Header row: corner + severity column headers */}
        <Box role="row" sx={{ display: 'flex', bgcolor: 'action.hover' }}>
          <Box sx={{ ...headerCellSx, flex: `0 0 ${AXIS_COL_WIDTH}px` }}>
            <Typography variant="caption" color="text.secondary">
              Probabilidade&nbsp;\&nbsp;Severidade
            </Typography>
          </Box>
          {severities.map((sev) => (
            <Box key={sev} role="columnheader" sx={{ ...headerCellSx, flex: 1 }}>
              <Typography variant="caption" fontWeight={700}>
                {sev}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {sevLabels[sev]}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* One row per probability level */}
        {probabilities.map((prob) => (
          <Box
            key={prob}
            role="row"
            sx={{ display: 'flex', borderTop: 1, borderColor: 'divider' }}
          >
            <Box
              role="rowheader"
              sx={{ ...headerCellSx, flex: `0 0 ${AXIS_COL_WIDTH}px`, bgcolor: 'action.hover' }}
            >
              <Typography variant="caption" fontWeight={700}>
                {prob}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {probLabels[prob]}
              </Typography>
            </Box>
            {severities.map((sev) => {
              const level = matrix[prob - 1]?.[sev - 1];
              const isSelected =
                selectedProbability === prob && selectedSeverity === sev;
              return (
                <Box
                  key={sev}
                  role="gridcell"
                  aria-selected={isSelected}
                  data-testid={`risk-cell-${prob}-${sev}`}
                  sx={{
                    flex: 1,
                    minHeight: 46,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.5,
                    textAlign: 'center',
                    bgcolor: level ? RISK_LEVEL_COLORS[level] : 'transparent',
                    color: '#fff',
                    fontWeight: isSelected ? 700 : 500,
                    borderLeft: '1px solid rgba(255,255,255,0.35)',
                    outline: isSelected ? '3px solid #111' : undefined,
                    outlineOffset: '-3px',
                    zIndex: isSelected ? 1 : undefined,
                  }}
                >
                  <Typography variant="caption" sx={{ lineHeight: 1.15, fontWeight: 'inherit' }}>
                    {level ? RISK_LEVEL_SHORT_LABELS[level] : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default RiskMatrixGrid;
